export interface ProgressTrackerOptions {
  animationSpeed?: number; // 애니메이션 속도 조절 (0-1)
  minStep?: number; // 최소 진행 단계
}

/**
 * 애니메이션 진행을 처리하는 유틸리티 클래스
 */
export class AnimationProgress {
  // 현재 진행률
  private current = 0;

  // 목표 진행률
  private target = 0;

  private animationFrameId: number | null = null;

  constructor(
    initialValue = 0,
    private options: ProgressTrackerOptions = {},
  ) {
    this.current = initialValue;
    this.target = initialValue;
  }

  /**
   * 진행률 애니메이션을 취소합니다.
   */
  public cancel(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * 새로운 목표 진행률로 애니메이션을 시작합니다.
   * @param targetValue 목표 진행률 값
   * @param onUpdate 각 프레임마다 호출되는 콜백 함수
   * @param onComplete 애니메이션 완료 시 호출되는 콜백 함수
   */
  public animateTo(targetValue: number, onUpdate: (value: number) => void, onComplete?: () => void): void {
    // 진행 중인 애니메이션 취소
    this.cancel();

    // 새 목표값 설정
    this.target = targetValue;

    // 옵션 설정
    const animationSpeed = this.options.animationSpeed ?? 0.1;
    const minStep = this.options.minStep ?? 0.1;

    // 애니메이션 함수
    const animate = () => {
      const step = (this.target - this.current) * animationSpeed;

      // 목표에 충분히 가까우면 바로 설정
      if (Math.abs(step) < minStep) {
        this.current = this.target;
        onUpdate(this.current);
        this.animationFrameId = null;
        onComplete?.();
        return;
      }

      // 아니면, 증가시키고 애니메이션 계속
      this.current += step;
      onUpdate(this.current);
      this.animationFrameId = requestAnimationFrame(animate);
    };

    // 애니메이션 시작
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * 현재 진행률 값을 반환합니다.
   */
  public getCurrentValue(): number {
    return this.current;
  }

  /**
   * 목표 진행률 값을 반환합니다.
   */
  public getTargetValue(): number {
    return this.target;
  }

  /**
   * 현재 애니메이션이 진행 중인지 여부를 반환합니다.
   */
  public isAnimating(): boolean {
    return this.animationFrameId !== null;
  }
}
